import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, User, AlertCircle } from 'lucide-react';
import api from '../api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    // Check for persisted error from sessionStorage
    const savedError = sessionStorage.getItem('loginError');
    if (savedError) {
      sessionStorage.removeItem('loginError');
      return savedError;
    }
    return '';
  });
  const [theme, setTheme] = useState(null);
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await api.get('/config/theme');
        setTheme(response.data);
      } catch (error) {
        console.log('Could not fetch theme, using defaults');
      }
    };
    fetchTheme();
  }, []);

  const logoUrl = branding.company_logo_url || theme?.logo_url || 'https://www.i4.com.ar/images/i4logo.png';
  const appTitle = branding.company_name || theme?.app_title || 'I4 ASSETS';
  const loginSubtitle = branding.login_title || 'Panel de Administración';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const formUsername = username;
    const formPassword = password;
    
    if (!formUsername || !formPassword) {
      const msg = 'Por favor complete todos los campos';
      setError(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);
    try {
      await login(formUsername, formPassword);
      toast.success('Bienvenido!');
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Credenciales inválidas';
      // Save error to sessionStorage in case component remounts
      sessionStorage.setItem('loginError', errorMsg);
      setError(errorMsg);
      setUsername(formUsername);
      setPassword('');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-bg flex items-center justify-center p-4">
      <div className="login-card">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={logoUrl} 
            alt={appTitle} 
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-800">{appTitle}</h1>
          <p className="text-slate-500 text-sm mt-1">{loginSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-semibold text-slate-700">Usuario</Label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="username"
                type="text"
                placeholder="Ingrese su usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-nerve pl-10 h-11"
                autoComplete="off"
                data-testid="login-username"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="Ingrese su contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-nerve pl-10 h-11"
                autoComplete="off"
                data-testid="login-password"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2" data-testid="login-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <Button 
            type="submit" 
            className="btn-nerve btn-nerve-primary w-full h-11"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Ingresando...
              </span>
            ) : 'Ingresar'}
          </Button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-8">
          © {new Date().getFullYear()} {branding.company_name} - {branding.footer_text}
        </p>
      </div>
    </div>
  );
};

export default Login;
